<template>
  <EditorWrapper class="max-w-3xl mx-auto rounded-lg p-12 mb-8 fadeIn">
    <h1 class="mb-6 text-xl">
      {{ editMode ? "Update" : "Create New" }} XP Condition
    </h1>
    <div class="edyst-form-group">
      <div class="flex justify-between items-center mt-3">
        <label for="name" class="label">
          Name
        </label>
        <div class="w-full">
          <input
            id="name"
            v-model="xpCondition.name"
            v-validate="'required'"
            type="text"
            autocomplete="off"
            name="name"
            placeholder="Write XP Point Condition name here"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("name") }}
          </small>
        </div>
      </div>
      <div class="flex justify-between items-center mt-3">
        <label for="points" class="label">
          XP Points
        </label>
        <div class="w-full">
          <input
            id="points"
            v-model="xpCondition.xp_points"
            v-validate="'required|numeric|min_value:1'"
            type="number"
            name="points"
            placeholder="Enter Number of XP Points"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("points") }}
          </small>
        </div>
      </div>
      <div class="flex justify-start items-center mt-3">
        <label for="redemption" class="label">
          Max Redemption
        </label>
        <div class="w-full">
          <BaseNumberIncrement
            id="redemption"
            v-model="xpCondition.max_count"
            v-validate="'required|numeric|min_value:1'"
            name="redemption"
            :min="1"
            @click.native.prevent="() => {}"
          />
          <input
            v-validate="'required|numeric|min_value:1'"
            :value="xpCondition.max_count"
            type="hidden"
            name="redemption"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("redemption") }}
          </small>
        </div>
      </div>
      <div class="flex justify-between items-center mt-3">
        <label for="component" class="label">
          Component
        </label>
        <div class="w-full">
          <VueSelect
            v-model="xpCondition.component"
            v-validate="'required'"
            :clearable="true"
            :options="componentOptions"
            :disabled="editMode"
            name="component"
            class="w-full"
            placeholder="Select Component"
            style="background:#f2f7fa"
            @input="handleComponentSelect"
          />
          <input
            v-validate="'required'"
            :value="xpCondition.component"
            type="hidden"
            name="component"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("component") }}
          </small>
        </div>
      </div>
      <div class="flex justify-start items-center mt-3">
        <label for="action" class="label">
          Action
          <i
            class="material-icons text-base text-grey"
            title="The action decides whether this xp condition will add or subtract xp points"
            >info</i
          >
        </label>
        <div class="w-1/4">
          <VueSelect
            v-model="xpCondition.action"
            v-validate="'required'"
            :clearable="false"
            :options="actions"
            :disabled="editMode"
            name="action"
            class="w-full"
            placeholder="Select Action"
            style="background:#f2f7fa"
          />
          <input
            v-validate="'required'"
            :value="xpCondition.action"
            type="hidden"
            name="action"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("action") }}
          </small>
        </div>
      </div>
      <div
        class="flex justify-between items-start mt-3"
        :class="{ 'pointer-events-none opacity-75 select-none': editMode }"
      >
        <label for="conditions" class="label">
          Conditions (Criteria)
        </label>
        <div class="w-full conditions">
          <div
            v-for="(item, idx) in xpCondition.condition"
            :key="idx"
            class="flex items-center relative condition-item"
          >
            <!-- <span class="counter text-sm">{{ idx + 1 }}.</span> -->
            <VueSelect
              v-model="item.value"
              v-validate="'required'"
              :clearable="false"
              :options="getConditionForComponent(xpCondition.component)"
              :disabled="editMode || !xpCondition.component"
              name="action"
              class="w-full"
              placeholder="Select condition value"
              style="background:#f2f7fa"
            />
            <!-- Disabled the dynamic behavior of xp conditions
                as we don't have enough matching conditions to select from now -->

            <!-- <button
              class="material-icons material-symbols-outlined text-grey cursor-pointer hover:text-red active:text-red focus:text-red"
              tabindex="0"
              :disabled="editMode"
              title="Remove Condition"
              @click="removeCondition(idx)"
            >
              delete_forever
            </button> -->
          </div>

          <!-- <button
            v-if="!editMode"
            class="text-white px-1 cursor-pointer bg-grey-dark hover:bg-grey-darker active:bg-grey-darker focus:bg-grey-darker rounded flex justify-center items-center w-1/4 text-sm mt-3 mb-2"
            tabindex="0"
            title="Add New Condition"
            :disabled="editMode"
            @click="addNewCondition"
          >
            <i class="material-icons material-symbols-outlined">
              add
            </i>
            Add Condition
          </button>
          <i v-if="!editMode" class="text-xs"
            >!! Make sure to double check the conditions. Conditions can't be
            updated/changed once the XP Condition is created</i
          > -->
        </div>
      </div>
      <div class="flex justify-between items-start mt-6">
        <label class="label">
          Description
        </label>
        <div class="w-full">
          <QuillEditor
            v-model="xpCondition.description"
            :options="editorOptions"
            class="description-field"
          />
          <input
            v-validate="'required'"
            :value="xpCondition.description"
            type="hidden"
            name="description"
          />
          <small class="err-msg text-red block mt-1">
            {{ veeValidateErrors.first("description") }}
          </small>
        </div>
      </div>
      <div v-if="editMode" class="flex justify-start items-center mt-6">
        <label class="label">
          Status
        </label>
        <div
          v-if="isActivating(xpCondition.id)"
          class="text-green"
          style="min-width: 10rem"
        >
          {{ getStatusById(xpCondition.id).status | capitalize }}
          <ProgressBar
            :progress="getStatusById(xpCondition.id).progress"
            class="text-green text-xs"
          />
        </div>
        <div v-else class="flex justify-start items-center">
          <ToggleButton
            v-model="getState"
            activeColor="#1ACD62"
            @click.native="
              handleToggleBtnClick(
                xpCondition.state === 'active' ? 'deactivate' : 'activate'
              )
            "
          />
          <span
            class="ml-4"
            :style="xpCondition.state === 'active' && { color: '#1ACD62' }"
          >
            {{ xpCondition.state | capitalize }}
          </span>
        </div>
      </div>
      <div class="flex justify-end items-center mt-8">
        <BaseButton
          class="inline-block px-12 py-2 mr-4"
          btnStyle="link"
          @click.prevent="cancel"
        >
          Cancel
        </BaseButton>
        <BaseButton
          class="inline-block px-12 py-2"
          btnStyle="blue"
          @click="handleSubmit"
        >
          {{ editMode ? "Update" : "Create" }}
        </BaseButton>
      </div>
    </div>
  </EditorWrapper>
</template>

<script>
import VueSelect from "vue-select"
import {
  fetchAllComponents,
  createXPCondition,
  updateXPCondition,
  fetchXPConditionById,
} from "@/apis/xpConditions"
import EditorWrapper from "@/components/shared/EditorWrapper"
import xpConditionMixin from "@/mixins/xpConditionMixin"
import BaseNumberIncrement from "@/components/Base/BaseNumberIncrement"
import ToggleButton from "@/components/shared/ToggleButton"
import QuillEditor from "@/components/Quill/QuillEditor"
import BaseButton from "@/components/Base/BaseButton"
import ProgressBar from "@/components/shared/ProgressBar"

export default {
  components: {
    EditorWrapper,
    VueSelect,
    BaseNumberIncrement,
    ToggleButton,
    QuillEditor,
    BaseButton,
    ProgressBar,
  },
  mixins: [xpConditionMixin],
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      xpCondition: {
        name: "",
        xp_points: 0,
        max_count: 1,
        component: "",
        description: "",
        state: "inactive",
        action: "add",
        condition: [{ value: "", type: "text" }],
      },
      componentOptions: [],
    }
  },
  computed: {
    criteriaConditions() {
      return {
        sign_up: ["REGISTERED"],
        first_coding_question_completion: ["completed"],
        signup_referral: ["true"],
        purchase_referral: ["captured"],
      }
    },
    actions() {
      return ["add", "subtract"]
    },
    getState: {
      get() {
        return this.xpCondition.state === "active"
      },
      set(newVal) {
        this.xpCondition.state = newVal ? "active" : "inactive"
      },
    },
    editorOptions() {
      return {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ script: "sub" }, { script: "super" }],
            ["blockquote", "code-block"],
            [{ indent: "-1" }, { indent: "+1" }],
            ["image"],
            ["clean"],
          ],
        },
        placeholder: "Write Description Here...",
      }
    },
  },
  async created() {
    this.populateComponents()
    if (this.editMode) {
      try {
        const res = await fetchXPConditionById(this.$route.params.id)
        // convert condition object to an array for frontend operations
        res.data.item.condition = Object.keys(res.data.item.condition).map(
          key => ({
            type:
              typeof res.data.item.condition[key] === "number"
                ? "number"
                : "text",
            value: res.data.item.condition[key],
          })
        )
        this.xpCondition = res.data.item
      } catch (err) {
        this.$noty.error("Failed to get details of the xp condition")
      }
    }
  },
  methods: {
    handleComponentSelect(component) {
      // populate/reset selected condition upon component change
      this.xpCondition.condition = [
        { value: this.getConditionForComponent(component)[0], type: "text" },
      ]
    },
    getConditionForComponent(component) {
      return this.criteriaConditions[component] || []
    },
    addNewCondition() {
      if (!this.editMode) {
        this.xpCondition.condition.push({ type: "text", value: "" })
      }
    },
    removeCondition(idx) {
      if (!this.editMode) {
        this.xpCondition.condition.splice(idx, 1)
      }
    },
    handleSubmit() {
      if (this.editMode) this.updateCondition()
      else this.addCondition()
    },
    async updateCondition() {
      const result = await this.$validator.validateAll()
      if (!result) {
        this.$noty.error("Please fix the errors and try again")
        return
      }
      const payload = {
        name: this.xpCondition.name,
        xp_points: parseInt(this.xpCondition.xp_points),
        max_count: this.xpCondition.max_count,
        description: this.xpCondition.description,
      }
      const conditions = {}
      this.xpCondition.condition.forEach((item, idx) => {
        conditions[`condition_${idx + 1}`] =
          item.type === "number" ? parseInt(item.value) : item.value.toString()
      })
      payload.condition = conditions
      updateXPCondition(this.xpCondition.id, payload)
        .then(() => {
          this.$noty.success(`XP Condition updated!`)
          setTimeout(() => {
            this.$router.push({ name: "XPConditionList" })
          }, 1000)
        })
        .catch(err => {
          const msg =
            err.response && err.response.data
              ? this.$options.filters.capitalize(err.response.data.message)
              : "Failed to update the XP Condition. Please try again"
          this.$swal({
            icon: "warning",
            text: msg,
          })
        })
    },
    async addCondition() {
      const result = await this.$validator.validateAll()
      if (!result) {
        this.$noty.error("Please fix the errors and try again")
        return
      }
      const payload = { ...this.xpCondition }
      const conditions = {}
      this.xpCondition.condition.forEach((item, idx) => {
        conditions[`condition_${idx + 1}`] =
          item.type === "number" ? parseInt(item.value) : item.value.toString()
      })
      payload.condition = conditions
      createXPCondition(payload)
        .then(() => {
          this.$noty.success(`New XP Condition created: ${payload.name}`)
          setTimeout(() => {
            this.$router.push({ name: "XPConditionList" })
          }, 1000)
        })
        .catch(() => {
          this.$noty.error(
            "Failed to create the XP Condition. Please try again"
          )
        })
    },
    async handleToggleBtnClick(actionType) {
      const value = await this.confirmToggle(this.xpCondition.name, actionType)
      if (!value) {
        this.getState = !this.getState
        return
      }
      this.updateActiveStatus({
        name: this.xpCondition.name,
        id: this.xpCondition.id,
        actionType,
      }).catch(() => {
        this.getState = !this.getState
      })
    },
    populateComponents() {
      fetchAllComponents()
        .then(res => {
          this.componentOptions = res.data.data
        })
        .catch(() => {
          this.$noty.error("Failed to get the components")
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.label {
  display: inline-block;
  min-width: 13rem;
}

.description-field {
  overflow: auto;
  max-height: 60vh;
}

.counter {
  position: absolute;
  top: 0.6rem;
  left: -1rem;
}

.condition-item {
  max-width: 25rem;
}

.edyst-form-group .conditions input,
.edyst-form-group .conditions select {
  @apply mt-1 text-sm;
}

.edyst-form-group .conditions label {
  @apply text-sm pt-0;
}
</style>
